import { useState, useEffect } from "react"
import axios from "axios";
import MuiAlert from "../utils/muiAlerts"
import { Helmet } from "react-helmet-async";
import { ListLoadingSkeleton } from "./LoadingSkeleton";
import EmptyState from "./EmptyState";
import Select from "react-select"
import * as React from "react";
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import {
    Box, Paper, Typography, Grid, TextField, Button, CircularProgress,
    Accordion, AccordionSummary, AccordionDetails, Divider,
    FormGroup, FormControlLabel, Switch, Chip, useTheme, Alert, Checkbox
} from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';

import { useFirebase } from "../contexts/firebase-context";
import AuthWrapper from "./AuthWrapper";
import UnifiedAdminControl from "./UnifiedAdminControl";
import { backendOrigin } from "../utils/backendOrigin";
import FileDropzone from "./FileDropzone";
import ModuleHeader from "./ModuleHeader";
import SWDiagnosticsCard from "./SWDiagnosticsCard";
import { dropDownStyle } from "../utils/commonUtils";
import { invalidateCaches } from "../utils/mutationUtils";
import { hasUnsafeTextInObject, sanitizeText } from "../utils/validationUtils";
import { useCachePolicySettings } from "../query/useCachePolicySettings";
import { useBranchListQuery, useDoctorListQuery, useSalespersonListQuery } from "../query/useReferenceDataQueries";

export const defaultAccess = {
    admin_panel: false,
    audiometry: false,
    generate_invoice: false,
    inventory: false,
    sales_report: false,
    patients: false,
    service: false
}

const ACTIVITY_RETENTION_DAYS = 30
const DEFAULT_CACHE_POLICY_DRAFT = {
    reference_data_ttl_seconds: 600,
    dashboard_reports_ttl_seconds: 180,
    monthly_report_ttl_seconds: 300,
    paged_records_ttl_seconds: 60,
    notify_on_stale_data: true,
}

const AdminPanel = () => {
    const theme = useTheme()
    const { currentUserInfo } = useFirebase()

    // ✅ NEW: Admin authorization check (from Firestore, not .env)
    const [adminList, setAdminList] = useState([])
    const [isUserAdmin, setIsUserAdmin] = useState(false)
    const [isLoadingAdminStatus, setIsLoadingAdminStatus] = useState(true)
    const [adminAuthError, setAdminAuthError] = useState(null)

    const [salespersonName, setSalespersonName] = useState("")
    const [salespersonApiState, setSalespersonApiState] = useState(false)

    const [branchName, setBranchName] = useState("")
    const [branchInvoiceCode, setBranchInvoiceCode] = useState("")
    const [branchApiState, setBranchApiState] = useState(false)

    const [doctorName, setDoctorName] = useState("")
    const [selectedDoctorQualification, setSelectedDoctorQualification] = useState(null)
    const [doctorRegistrationNumber, setDoctorRegistrationNumber] = useState("")
    const [doctorSignatureFile, setDoctorSignatureFile] = useState(null)
    const [doctorApiState, setDoctorApiState] = useState(false)

    const [scriptApiState, setScriptApiState] = useState(false)
    const [backupPassword, setBackupPassword] = useState("")
    const [backupDryRun, setBackupDryRun] = useState(true)
    const [backupApplyAcknowledge, setBackupApplyAcknowledge] = useState(false)
    const [lastBackupRun, setLastBackupRun] = useState(null)

    const [activityLogs, setActivityLogs] = useState([])
    const [activityLogsCursor, setActivityLogsCursor] = useState(null)
    const [activityLogsHasMore, setActivityLogsHasMore] = useState(false)
    const [activityLogsLoading, setActivityLogsLoading] = useState(false)
    const [activityMethodFilter, setActivityMethodFilter] = useState("")
    const [activityActorUidFilter, setActivityActorUidFilter] = useState("")
    const [activityPathFilter, setActivityPathFilter] = useState("")
    const [activityLogsExpanded, setActivityLogsExpanded] = useState(true)
    const [activityRetentionLoading, setActivityRetentionLoading] = useState(false)
    const [activityRetentionInfo, setActivityRetentionInfo] = useState(null)
    const [activityRetentionDismissed, setActivityRetentionDismissed] = useState(false)
    const [selectedActivityLogIds, setSelectedActivityLogIds] = useState([])
    const [activityDeleteLoading, setActivityDeleteLoading] = useState(false)
    const [activityLastRefreshedAt, setActivityLastRefreshedAt] = useState(null)
    const [expandedActivityLogId, setExpandedActivityLogId] = useState(null)
    const [cachePolicyDraft, setCachePolicyDraft] = useState(DEFAULT_CACHE_POLICY_DRAFT)

    const {
        data: cachePolicySettings,
        isLoading: isCachePolicyLoading,
        isFetching: isCachePolicyFetching,
        saveCachePolicy,
        isSaving: isCachePolicySaving,
    } = useCachePolicySettings(currentUserInfo)

    const { data: salespersonList = [] } = useSalespersonListQuery(currentUserInfo)
    const { data: branchList = [] } = useBranchListQuery(currentUserInfo)
    const { data: doctorList = [] } = useDoctorListQuery(currentUserInfo)

    // ✅ NEW: Fetch admin list from Firestore & check authorization
    useEffect(() => {
        const checkAdminStatus = async () => {
            if (!currentUserInfo) {
                setIsLoadingAdminStatus(false)
                setIsUserAdmin(false)
                return
            }

            try {
                setIsLoadingAdminStatus(true)
                const token = await currentUserInfo.getIdToken()
                
                const response = await axios.get(`${backendOrigin}/admin-uids`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                })

                if (response.data && response.data.admin_uids) {
                    const admins = response.data.admin_uids
                    setAdminList(admins)
                    
                    // ✅ Check if current user is in the Firestore admin list
                    const userIsAdmin = admins.includes(currentUserInfo.uid)
                    setIsUserAdmin(userIsAdmin)
                    setAdminAuthError(userIsAdmin ? null : 'You are not authorized as admin')
                } else {
                    setAdminList([])
                    setIsUserAdmin(false)
                    setAdminAuthError('Failed to load admin list')
                }
            } catch (error) {
                console.error('Error checking admin status:', error)
                setAdminList([])
                setIsUserAdmin(false)
                setAdminAuthError(error.response?.data?.message || error.message)
            } finally {
                setIsLoadingAdminStatus(false)
            }
        }

        checkAdminStatus()
    }, [currentUserInfo])

    useEffect(() => {
        if (!cachePolicySettings) return
        setCachePolicyDraft({
            reference_data_ttl_seconds: cachePolicySettings.reference_data_ttl_seconds ?? DEFAULT_CACHE_POLICY_DRAFT.reference_data_ttl_seconds,
            dashboard_reports_ttl_seconds: cachePolicySettings.dashboard_reports_ttl_seconds ?? DEFAULT_CACHE_POLICY_DRAFT.dashboard_reports_ttl_seconds,
            monthly_report_ttl_seconds: cachePolicySettings.monthly_report_ttl_seconds ?? DEFAULT_CACHE_POLICY_DRAFT.monthly_report_ttl_seconds,
            paged_records_ttl_seconds: cachePolicySettings.paged_records_ttl_seconds ?? DEFAULT_CACHE_POLICY_DRAFT.paged_records_ttl_seconds,
            notify_on_stale_data: typeof cachePolicySettings.notify_on_stale_data === 'boolean'
                ? cachePolicySettings.notify_on_stale_data
                : DEFAULT_CACHE_POLICY_DRAFT.notify_on_stale_data,
        })
    }, [cachePolicySettings])

    const updateCachePolicyField = (key, value) => {
        setCachePolicyDraft((prev) => ({ ...prev, [key]: value }))
    }

    const saveCachePolicyFromAdmin = async () => {
        const payload = {
            reference_data_ttl_seconds: Number(cachePolicyDraft.reference_data_ttl_seconds),
            dashboard_reports_ttl_seconds: Number(cachePolicyDraft.dashboard_reports_ttl_seconds),
            monthly_report_ttl_seconds: Number(cachePolicyDraft.monthly_report_ttl_seconds),
            paged_records_ttl_seconds: Number(cachePolicyDraft.paged_records_ttl_seconds),
            notify_on_stale_data: Boolean(cachePolicyDraft.notify_on_stale_data),
        }

        if ([
            payload.reference_data_ttl_seconds,
            payload.dashboard_reports_ttl_seconds,
            payload.monthly_report_ttl_seconds,
            payload.paged_records_ttl_seconds,
        ].some((x) => Number.isNaN(x) || x <= 0)) {
            MuiAlert.fire('Oops', 'TTL values must be positive numbers', 'warning')
            return
        }

        await saveCachePolicy(payload)
    }

    const fetchActivityLogs = ({ reset = false } = {}) => {
        if (!currentUserInfo) {
            return
        }

        setActivityLogsLoading(true)

        const payload = {
            limit: 50,
            cursor: reset ? null : activityLogsCursor,
            actor_uid: activityActorUidFilter.trim(),
            method: activityMethodFilter.trim().toUpperCase(),
            path_contains: activityPathFilter.trim(),
            current_user_uid: currentUserInfo.uid,
            current_user_name: currentUserInfo.displayName,
        }

        return axios.post(`${backendOrigin}/get-activity-logs`, payload, { headers: { 'Content-Type': 'application/json' } })
            .then((res) => {
                if (res.data.operation === "success") {
                    const info = res.data.info || {}
                    const items = info.items || []
                    if (reset) {
                        setActivityLogs(items)
                        setSelectedActivityLogIds((prev) => prev.filter((id) => items.some((entry) => entry.id === id)))
                    } else {
                        setActivityLogs((prev) => [...prev, ...items])
                    }
                    setActivityLogsCursor(info.nextCursor || null)
                    setActivityLogsHasMore(Boolean(info.hasMore))
                    setActivityLastRefreshedAt(new Date().toISOString())
                } else {
                    MuiAlert.fire('Oops!', res.data.message, 'error')
                }
            })
            .catch((err) => {
                console.error(err)
                MuiAlert.fire('Error!!', err.message, 'error')
            })
            .finally(() => {
                setActivityLogsLoading(false)
            })
    }

    useEffect(() => {
        if (currentUserInfo) {
            fetchActivityLogs({ reset: true })
            fetchActivityRetention()
            setActivityRetentionDismissed(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUserInfo])

    const fetchActivityRetention = ({ silent = false } = {}) => {
        if (!currentUserInfo) {
            return
        }

        if (!silent) {
            setActivityRetentionLoading(true)
        }

        return axios.post(`${backendOrigin}/get-activity-log-retention`, {
            days: ACTIVITY_RETENTION_DAYS,
            current_user_uid: currentUserInfo.uid,
            current_user_name: currentUserInfo.displayName,
        }, { headers: { 'Content-Type': 'application/json' } })
            .then((res) => {
                if (res.data.operation === "success") {
                    const info = res.data.info || null
                    setActivityRetentionInfo(info)
                    if (!info?.oldCount) {
                        setActivityRetentionDismissed(false)
                    }
                } else {
                    MuiAlert.fire('Oops!', res.data.message, 'error')
                }
            })
            .catch((err) => {
                console.error(err)
                MuiAlert.fire('Error!!', err.message, 'error')
            })
            .finally(() => {
                if (!silent) {
                    setActivityRetentionLoading(false)
                }
            })
    }

    const toggleActivityLogSelection = (logId) => {
        setSelectedActivityLogIds((prev) => prev.includes(logId)
            ? prev.filter((id) => id !== logId)
            : [...prev, logId])
    }

    const selectVisibleActivityLogs = () => {
        setSelectedActivityLogIds(activityLogs.map((entry) => entry.id))
    }

    const clearSelectedActivityLogs = () => {
        setSelectedActivityLogIds([])
    }

    const refreshActivityAdminData = () => {
        fetchActivityLogs({ reset: true })
        fetchActivityRetention()
        setExpandedActivityLogId(null)
    }

    const deleteActivityLogs = (ids) => {
        if (!currentUserInfo || !Array.isArray(ids) || ids.length === 0) {
            return
        }

        const confirmed = window.confirm(`Delete ${ids.length} activity log(s)? This cannot be undone.`)
        if (!confirmed) {
            return
        }

        setActivityDeleteLoading(true)
        axios.post(`${backendOrigin}/delete-activity-logs`, {
            ids,
            current_user_uid: currentUserInfo.uid,
            current_user_name: currentUserInfo.displayName,
        }, { headers: { 'Content-Type': 'application/json' } })
            .then((res) => {
                if (res.data.operation === "success") {
                    MuiAlert.fire('Success!', res.data.message, 'success')
                    setSelectedActivityLogIds((prev) => prev.filter((id) => !ids.includes(id)))
                    refreshActivityAdminData()
                } else {
                    MuiAlert.fire('Oops!', res.data.message, 'error')
                }
            })
            .catch((err) => {
                console.error(err)
                MuiAlert.fire('Error!!', err.message, 'error')
            })
            .finally(() => {
                setActivityDeleteLoading(false)
            })
    }

    const deleteOldActivityLogs = () => {
        if (!currentUserInfo || !activityRetentionInfo?.oldCount) {
            return
        }

        const confirmed = window.confirm(`Delete ${activityRetentionInfo.oldCount} activity log(s) older than ${ACTIVITY_RETENTION_DAYS} days? This cannot be undone.`)
        if (!confirmed) {
            return
        }

        setActivityDeleteLoading(true)
        axios.post(`${backendOrigin}/delete-old-activity-logs`, {
            days: ACTIVITY_RETENTION_DAYS,
            current_user_uid: currentUserInfo.uid,
            current_user_name: currentUserInfo.displayName,
        }, { headers: { 'Content-Type': 'application/json' } })
            .then((res) => {
                if (res.data.operation === "success") {
                    MuiAlert.fire('Success!', res.data.message, 'success')
                    setActivityRetentionDismissed(false)
                    setSelectedActivityLogIds([])
                    refreshActivityAdminData()
                } else {
                    MuiAlert.fire('Oops!', res.data.message, 'error')
                }
            })
            .catch((err) => {
                console.error(err)
                MuiAlert.fire('Error!!', err.message, 'error')
            })
            .finally(() => {
                setActivityDeleteLoading(false)
            })
    }

    const inputSx = {
        '& .MuiOutlinedInput-root': {
            backgroundColor: theme.palette.mode === 'dark' ? '#1e293b' : '#fff',
            '& fieldset': { borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.6)' : '#000', borderWidth: 2 },
            '&:hover fieldset': { borderColor: theme.palette.mode === 'dark' ? '#fff' : '#000' },
            '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main }
        },
        '& .MuiInputBase-input': {
            color: theme.palette.mode === 'dark' ? '#e5e7eb' : '#000',
        }
    }

    const panelCard = {
        p: 2.5,
        mb: 2,
        border: `1.5px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.13)'}`,
        borderRadius: 3,
        background: theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, rgba(30,58,95,0.18) 0%, rgba(15,32,64,0.18) 100%)'
            : 'linear-gradient(135deg, #f0f7ff 0%, #fafbff 100%)'
    }

            const isBackupActionDisabled = scriptApiState || !backupPassword || (!backupDryRun && !backupApplyAcknowledge)

    return (
        <>
            <Helmet>
                <meta name="description" content="Happy Ears Kolkata is a React-powered app for efficient hearing care management, offering seamless invoice creation, inventory control, and secure patient data storage with integrated location tracking, created by Hritwick De. Administrator Page to be accessed by authorized personnel only for additional features" />
                <title>Admin Panel | Happy Ears Kolkata Invoicing </title>
            </Helmet>

            <div className="module-page-shell">
                <ModuleHeader
                    title="Admin Panel"
                    subtitle="Manage users, access control, branches, doctors, and system scripts"
                    Icon={AdminPanelSettingsRoundedIcon}
                    color="#f59e0b"
                />

                <AuthWrapper page={"admin_panel"}>
                    {/* ✅ NEW: Loading admin status */}
                    {isLoadingAdminStatus ? (
                        <Box sx={{ px: { xs: 1, md: 4 }, pb: 4, textAlign: 'center', py: 4 }}>
                            <CircularProgress />
                            <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.secondary' }}>
                                Verifying admin authorization from Firestore...
                            </Typography>
                        </Box>
                    ) : !isUserAdmin ? (
                        // ✅ NEW: Not authorized - show error
                        <Box sx={{ px: { xs: 1, md: 4 }, pb: 4 }}>
                            <Alert severity="error" sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                    ❌ Admin Authorization Required
                                </Typography>
                                <Typography variant="body2">
                                    {adminAuthError || 'You do not have admin access. Your UID is not in the Firestore admin list (app_settings/admin_uids_v1).'}
                                </Typography>
                                {currentUserInfo && (
                                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                                        Your UID: <code>{currentUserInfo.uid}</code>
                                    </Typography>
                                )}
                                <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                                    Contact an existing admin to add you to the admin list.
                                </Typography>
                            </Alert>
                        </Box>
                    ) : (
                        // ✅ NEW: Authorized - show full admin panel
                    <Box sx={{ px: { xs: 1, md: 4 }, pb: 4 }}>
                        <SWDiagnosticsCard />

                        <Paper elevation={0} sx={panelCard}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Cache Policy (TanStack + Backend TTL)</Typography>
                            <Typography variant="caption" color="text.secondary">
                                Configure how long different data types remain fresh before showing stale warnings. Users can always manually refresh for live data.
                            </Typography>

                            <Grid container spacing={2} alignItems="end" sx={{ mt: 0.5 }}>
                                <Grid size={{ md: 3 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700 }}>Reference Data TTL (sec)</Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Affects: Branches, Doctors, Salespersons</Typography>
                                    <TextField type="number" size="small" fullWidth value={cachePolicyDraft.reference_data_ttl_seconds} onChange={(e) => updateCachePolicyField('reference_data_ttl_seconds', e.target.value)} sx={inputSx} />
                                </Grid>
                                <Grid size={{ md: 3 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700 }}>Reports/Dashboard TTL (sec)</Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Affects: Reports page summary & details</Typography>
                                    <TextField type="number" size="small" fullWidth value={cachePolicyDraft.dashboard_reports_ttl_seconds} onChange={(e) => updateCachePolicyField('dashboard_reports_ttl_seconds', e.target.value)} sx={inputSx} />
                                </Grid>
                                <Grid size={{ md: 3 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700 }}>Monthly Report TTL (sec)</Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Affects: SalesReport tab 2 (Monthly)</Typography>
                                    <TextField type="number" size="small" fullWidth value={cachePolicyDraft.monthly_report_ttl_seconds} onChange={(e) => updateCachePolicyField('monthly_report_ttl_seconds', e.target.value)} sx={inputSx} />
                                </Grid>
                                <Grid size={{ md: 3 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700 }}>Paged Records TTL (sec)</Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Affects: Inventory, Patients, Service, Audiometry, Invoices (all paginated tables)</Typography>
                                    <TextField type="number" size="small" fullWidth value={cachePolicyDraft.paged_records_ttl_seconds} onChange={(e) => updateCachePolicyField('paged_records_ttl_seconds', e.target.value)} sx={inputSx} />
                                </Grid>
                            </Grid>

                            <FormGroup sx={{ mt: 1 }}>
                                <FormControlLabel
                                    control={<Switch checked={cachePolicyDraft.notify_on_stale_data} onChange={(e) => updateCachePolicyField('notify_on_stale_data', e.target.checked)} />}
                                    label="Notify users when newer data is available"
                                />
                            </FormGroup>

                            <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                <Button variant="contained" color="warning" onClick={saveCachePolicyFromAdmin} disabled={isCachePolicySaving || isCachePolicyLoading}>
                                    {isCachePolicySaving ? <CircularProgress size={18} color="inherit" /> : 'Save Cache Policy'}
                                </Button>
                                <Button variant="outlined" color="inherit" onClick={() => setCachePolicyDraft(DEFAULT_CACHE_POLICY_DRAFT)}>
                                    Reset Draft
                                </Button>
                                <Chip size="small" variant="outlined" label={`Loading: ${isCachePolicyLoading || isCachePolicyFetching ? 'Yes' : 'No'}`} />
                                <Chip size="small" variant="outlined" label={`Source: ${cachePolicySettings?.source || 'default'}`} />
                            </Box>

                            <Divider sx={{ my: 1.5 }} />
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>Invalidation Keys</Typography>
                            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
                                {Object.entries(cachePolicySettings?.invalidation_keys || {}).map(([group, keys]) => (
                                    <Chip key={group} size="small" label={`${group}: ${(keys || []).join(', ')}`} />
                                ))}
                            </Box>
                        </Paper>

                        {/* ✅ UNIFIED ADMIN CONTROL (Recommended) */}
                        <Paper elevation={0} sx={panelCard}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Chip 
                                    label="⭐ Recommended" 
                                    size="small" 
                                    color="primary" 
                                    variant="outlined"
                                    sx={{ fontWeight: 700 }}
                                />
                            </Box>
                            <UnifiedAdminControl 
                                currentUserInfo={currentUserInfo}
                                adminList={adminList}
                            />
                        </Paper>



                        <Paper elevation={0} sx={panelCard}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Add Salesperson</Typography>
                            <Grid container spacing={2} alignItems="end">
                                <Grid size={{ md: 8 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700 }}>Salesperson Name *</Typography>
                                    <TextField fullWidth size="small" value={salespersonName} onChange={(e) => setSalespersonName(e.target.value.trim())} sx={inputSx} />
                                </Grid>
                                <Grid size={{ md: 4 }}>
                                    <Button fullWidth variant="contained" color="warning" disabled={salespersonApiState} onClick={() => {
                                        if (!salespersonName) {
                                            MuiAlert.fire("Oops", "Enter Salesperson name", "warning")
                                            return
                                        }
                                        if (hasUnsafeTextInObject(salespersonName)) {
                                            MuiAlert.fire("Oops", "Invalid characters in Salesperson name", "warning")
                                            return
                                        }

                                        let data = {
                                            salesperson_name: sanitizeText(salespersonName),
                                            current_user_uid: currentUserInfo.uid,
                                            current_user_name: currentUserInfo.displayName
                                        }

                                        setSalespersonApiState(true)
                                        axios.post(`${backendOrigin}/save-salesperson`, data, { headers: { 'Content-Type': 'application/json' } })
                                            .then(async (res) => {
                                                setSalespersonApiState(false)
                                                if (res.data.operation === "success") {
                                                    MuiAlert.fire('Success!', res.data.message, 'success');
                                                    await invalidateCaches(['salespersons'])
                                                    setSalespersonName("")
                                                } else {
                                                    MuiAlert.fire('Oops!', res.data.message, 'error');
                                                }
                                            })
                                            .catch((err) => {
                                                console.error(err)
                                                setSalespersonApiState(false)
                                                MuiAlert.fire('Error!!', err.message, 'error');
                                            })
                                    }}>
                                        {salespersonApiState ? <CircularProgress size={18} color="inherit" /> : '+ Add Salesperson'}
                                    </Button>
                                </Grid>
                            </Grid>
                            <Divider sx={{ mt: 2, mb: 1.5 }} />
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>Existing Salespersons</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
                                {salespersonList.length === 0
                                    ? <Typography variant="caption" color="text.disabled">None added yet</Typography>
                                    : salespersonList.map(s => <Chip key={s.id} label={s.salesperson_name} size="small" />)}
                            </Box>
                        </Paper>

                        <Paper elevation={0} sx={panelCard}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Add Branch</Typography>
                            <Grid container spacing={2} alignItems="end">
                                <Grid size={{ md: 5 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700 }}>Branch Name *</Typography>
                                    <TextField fullWidth size="small" value={branchName} onChange={(e) => setBranchName(e.target.value.trim())} sx={inputSx} />
                                </Grid>
                                <Grid size={{ md: 4 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700 }}>Branch Invoice Code *</Typography>
                                    <TextField fullWidth size="small" value={branchInvoiceCode} onChange={(e) => setBranchInvoiceCode(e.target.value.trim())} sx={inputSx} />
                                </Grid>
                                <Grid size={{ md: 3 }}>
                                    <Button fullWidth variant="contained" color="warning" disabled={branchApiState} onClick={() => {
                                        if (!branchName || !branchInvoiceCode) {
                                            MuiAlert.fire("Oops", "Enter all Branch details", "warning")
                                            return
                                        }
                                        if (hasUnsafeTextInObject([branchName, branchInvoiceCode])) {
                                            MuiAlert.fire("Oops", "Invalid characters in Branch details", "warning")
                                            return
                                        }

                                        let data = {
                                            branch_name: sanitizeText(branchName),
                                            branch_invoice_code: sanitizeText(branchInvoiceCode),
                                            current_user_uid: currentUserInfo.uid,
                                            current_user_name: currentUserInfo.displayName
                                        }

                                        setBranchApiState(true)
                                        axios.post(`${backendOrigin}/save-branch`, data, { headers: { 'Content-Type': 'application/json' } })
                                            .then(async (res) => {
                                                setBranchApiState(false)
                                                if (res.data.operation === "success") {
                                                    MuiAlert.fire('Success!', res.data.message, 'success');
                                                    await invalidateCaches(['branches'])
                                                    setBranchName("")
                                                    setBranchInvoiceCode("")
                                                } else {
                                                    MuiAlert.fire('Oops!', res.data.message, 'error');
                                                }
                                            })
                                            .catch((err) => {
                                                console.error(err)
                                                setBranchApiState(false)
                                                MuiAlert.fire('Error!!', err.message, 'error');
                                            })
                                    }}>
                                        {branchApiState ? <CircularProgress size={18} color="inherit" /> : '+ Add Branch'}
                                    </Button>
                                </Grid>
                            </Grid>
                            <Divider sx={{ mt: 2, mb: 1.5 }} />
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>Existing Branches</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
                                {branchList.length === 0
                                    ? <Typography variant="caption" color="text.disabled">None added yet</Typography>
                                    : branchList.map(b => <Chip key={b.id} label={`${b.branch_name} (${b.branch_invoice_code})`} size="small" />)}
                            </Box>
                        </Paper>

                        <Paper elevation={0} sx={panelCard}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Add Doctor</Typography>
                            <Grid container spacing={2} alignItems="end">
                                <Grid size={{ md: 4 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700 }}>Doctor Name *</Typography>
                                    <TextField fullWidth size="small" value={doctorName} onChange={(e) => setDoctorName(e.target.value)} sx={inputSx} />
                                </Grid>
                                <Grid size={{ md: 4 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700 }}>Doctor Qualification *</Typography>
                                    <Select
                                        options={["MASLP", "BASLP"].map(x => ({ label: x, value: x }))}
                                        value={selectedDoctorQualification ? { label: selectedDoctorQualification, value: selectedDoctorQualification } : null}
                                        onChange={(val) => setSelectedDoctorQualification(val.value)}
                                        styles={dropDownStyle(theme.palette.mode === 'dark')}
                                        placeholder="Select a Qualification..."
                                    />
                                </Grid>
                                <Grid size={{ md: 4 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700 }}>Registration Number *</Typography>
                                    <TextField fullWidth size="small" value={doctorRegistrationNumber} onChange={(e) => setDoctorRegistrationNumber(e.target.value.trim())} sx={inputSx} />
                                </Grid>
                                <Grid size={{ md: 9 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700 }}>Doctor Signature *</Typography>
                                    <FileDropzone
                                        uploadedFiles={doctorSignatureFile ? [doctorSignatureFile] : []}
                                        onFilesChange={(newFiles) => setDoctorSignatureFile(newFiles[0])}
                                        maxFiles={1}
                                        maxSize={2 * 1024 * 1024}
                                        accept={{ "image/png": [".png"] }}
                                    />
                                </Grid>
                                <Grid size={{ md: 3 }}>
                                    <Button fullWidth variant="contained" color="warning" disabled={doctorApiState} onClick={() => {
                                        if (!doctorName || !selectedDoctorQualification || !doctorRegistrationNumber || !doctorSignatureFile) {
                                            MuiAlert.fire("Oops", "Enter all Doctor details", "warning")
                                            return
                                        }
                                        if (hasUnsafeTextInObject([doctorName, doctorRegistrationNumber])) {
                                            MuiAlert.fire("Oops", "Invalid characters in Doctor details", "warning")
                                            return
                                        }

                                        let data = new FormData()
                                        data.append("doctor_name", sanitizeText(doctorName))
                                        data.append("doctor_qualification", selectedDoctorQualification)
                                        data.append("doctor_registration_number", sanitizeText(doctorRegistrationNumber))
                                        data.append("doctor_signature_file", doctorSignatureFile)
                                        data.append("current_user_uid", currentUserInfo.uid)
                                        data.append("current_user_name", currentUserInfo.displayName)

                                        setDoctorApiState(true)
                                        axios.post(`${backendOrigin}/save-doctor`, data, { headers: { 'Content-Type': 'multipart/form-data' } })
                                            .then(async (res) => {
                                                setDoctorApiState(false)
                                                if (res.data.operation === "success") {
                                                    MuiAlert.fire('Success!', res.data.message, 'success');
                                                    await invalidateCaches(['doctors'])
                                                    setDoctorName("")
                                                    setSelectedDoctorQualification(null)
                                                    setDoctorRegistrationNumber("")
                                                    setDoctorSignatureFile(null)
                                                } else {
                                                    MuiAlert.fire('Oops!', res.data.message, 'error');
                                                }
                                            })
                                            .catch((err) => {
                                                console.error(err)
                                                setDoctorApiState(false)
                                                MuiAlert.fire('Error!!', err.message, 'error');
                                            })
                                    }}>
                                        {doctorApiState ? <CircularProgress size={18} color="inherit" /> : '+ Add Doctor'}
                                    </Button>
                                </Grid>
                            </Grid>
                            <Divider sx={{ mt: 2, mb: 1.5 }} />
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>Existing Doctors</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
                                {doctorList.length === 0
                                    ? <Typography variant="caption" color="text.disabled">None added yet</Typography>
                                    : doctorList.map(d => <Chip key={d.id} label={`${d.doctor_name} (${d.doctor_qualification})`} size="small" />)}
                            </Box>
                        </Paper>

                        <Paper elevation={0} sx={panelCard}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Database Backup</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Admin-only backup action. Enter password <b>backup</b> to authorize execution.
                            </Typography>

                            <Alert severity={backupDryRun ? "info" : "warning"} sx={{ mb: 2 }}>
                                {backupDryRun
                                    ? 'Dry-run mode checks backup scope and counts only. No file is written.'
                                    : 'Apply mode writes a full backup file to server storage. Use only when required.'}
                            </Alert>

                            <Grid container spacing={2} alignItems="end">
                                <Grid size={{ md: 6 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700 }}>Action Type</Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        value="backup_database"
                                        disabled
                                        sx={inputSx}
                                    />
                                </Grid>
                                <Grid size={{ md: 6 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'error.main' }}>Backup Password * (Required)</Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="password"
                                        value={backupPassword}
                                        onChange={(e) => setBackupPassword(e.target.value)}
                                        placeholder="Secret backup password from .env"
                                        sx={inputSx}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                        ⚠️ This requires the BACKUP_SCRIPT_PASSWORD from backend .env
                                    </Typography>
                                </Grid>
                            </Grid>

                            <FormControlLabel
                                sx={{ mt: 1.5 }}
                                control={<Switch checked={backupDryRun} onChange={() => {
                                    setBackupDryRun((prev) => !prev)
                                    setBackupApplyAcknowledge(false)
                                }} />}
                                label={backupDryRun ? 'Dry-run only (no file write)' : 'Apply backup (writes backup file)'}
                            />

                            {!backupDryRun && (
                                <FormControlLabel
                                    sx={{ mt: 0.5 }}
                                    control={<Checkbox checked={backupApplyAcknowledge} onChange={() => setBackupApplyAcknowledge((prev) => !prev)} color="warning" />}
                                    label="I understand this writes a backup file to the server"
                                />
                            )}

                            <Box sx={{ mt: 1.5 }}>
                            <Button variant="contained" color="warning" disabled={isBackupActionDisabled} onClick={() => {
                                if (!backupPassword) {
                                    MuiAlert.fire('Oops!', 'Enter backup password to continue', 'warning');
                                    return;
                                }

                                if (!backupDryRun && !backupApplyAcknowledge) {
                                    MuiAlert.fire('Oops!', 'Acknowledge apply mode before running backup', 'warning');
                                    return;
                                }

                                if (!backupDryRun) {
                                    const confirmed = window.confirm('This will write a backup file to server storage. Continue?');
                                    if (!confirmed) {
                                        return;
                                    }
                                }

                                let data = {
                                    action_type: 'backup_database',
                                    action_password: backupPassword,
                                    dry_run: backupDryRun,
                                    current_user_uid: currentUserInfo.uid,
                                    current_user_name: currentUserInfo.displayName
                                }

                                setScriptApiState(true)
                                
                                // Use fetch for file download in apply mode, axios for dry-run
                                if (!backupDryRun) {
                                    // Apply mode: Download file
                                    fetch(`${backendOrigin}/custom-script`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify(data),
                                    })
                                    .then((res) => {
                                        setScriptApiState(false)
                                        
                                        if (res.ok) {
                                            // Extract filename from content-disposition header
                                            const disposition = res.headers.get('content-disposition');
                                            let fileName = 'firestore_backup.json';
                                            if (disposition && disposition.includes('filename')) {
                                                fileName = disposition.split('filename="')[1]?.split('"')[0] || fileName;
                                            }
                                            
                                            res.blob().then((blob) => {
                                                // Create download link
                                                const url = window.URL.createObjectURL(blob);
                                                const link = document.createElement('a');
                                                link.href = url;
                                                link.download = fileName;
                                                document.body.appendChild(link);
                                                link.click();
                                                document.body.removeChild(link);
                                                window.URL.revokeObjectURL(url);
                                                
                                                // Clear password and show success
                                                setBackupPassword("")
                                                setBackupApplyAcknowledge(false)
                                                setLastBackupRun({
                                                    status: 'success',
                                                    at: new Date().toISOString(),
                                                    details: { mode: 'apply', fileName: fileName },
                                                    message: `Backup downloaded successfully: ${fileName}`,
                                                })
                                                MuiAlert.fire('Success!', `Backup file downloaded: ${fileName}\n\nCheck your Downloads folder`, 'success');
                                            });
                                        } else {
                                            setBackupPassword("")
                                            res.json().then((errData) => {
                                                setLastBackupRun({
                                                    status: 'failed',
                                                    at: new Date().toISOString(),
                                                    details: null,
                                                    message: errData.message,
                                                })
                                                MuiAlert.fire('Error!', errData.message || 'Backup failed', 'error');
                                            }).catch(() => {
                                                MuiAlert.fire('Error!', 'Backup failed', 'error');
                                            });
                                        }
                                    })
                                    .catch((err) => {
                                        setScriptApiState(false)
                                        console.error(err)
                                        setBackupPassword("")
                                        setLastBackupRun({
                                            status: 'failed',
                                            at: new Date().toISOString(),
                                            details: null,
                                            message: err.message,
                                        })
                                        MuiAlert.fire('Error!!', err.message, 'error');
                                    })
                                } else {
                                    // Dry-run mode: Use axios for JSON response
                                    axios.post(`${backendOrigin}/custom-script`, data, { headers: { 'Content-Type': 'application/json' } })
                                        .then((res) => {
                                            setScriptApiState(false)
                                            if (res.data.operation === "success") {
                                                setBackupPassword("")
                                                setBackupApplyAcknowledge(false)
                                                const details = res.data?.info
                                                setLastBackupRun({
                                                    status: 'success',
                                                    at: new Date().toISOString(),
                                                    details: details || null,
                                                    message: res.data.message,
                                                })
                                                const detailText = details
                                                    ? `Collections: ${details.collectionCount}\nDocuments: ${details.documentCount}`
                                                    : ''
                                                MuiAlert.fire('Success!', `${res.data.message}${detailText ? `\n\n${detailText}` : ''}`, 'success');
                                            } else {
                                                setLastBackupRun({
                                                    status: 'failed',
                                                    at: new Date().toISOString(),
                                                    details: null,
                                                    message: res.data.message,
                                                })
                                                setBackupPassword("")
                                                MuiAlert.fire('Oops!', res.data.message, 'error');
                                            }
                                        })
                                        .catch((err) => {
                                            setScriptApiState(false)
                                            console.error(err)
                                            setLastBackupRun({
                                                status: 'failed',
                                                at: new Date().toISOString(),
                                                details: null,
                                                message: err.message,
                                            })
                                            setBackupPassword("")
                                            MuiAlert.fire('Error!!', err.message, 'error');
                                        })
                                }
                            }}>
                                {scriptApiState ? <CircularProgress size={18} color="inherit" /> : (backupDryRun ? 'Run Backup Dry-Run' : 'Run Backup')}
                            </Button>
                            </Box>

                            {lastBackupRun && (
                                <Paper variant="outlined" sx={{ mt: 2, p: 1.5 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>Last Backup Run</Typography>
                                    <Typography sx={{ mt: 0.5 }}>
                                        Status: <b>{lastBackupRun.status}</b>
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Time: {new Date(lastBackupRun.at).toLocaleString()}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Message: {lastBackupRun.message}
                                    </Typography>
                                    {lastBackupRun.details && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            {lastBackupRun.details.mode === 'apply' ? 
                                                `✅ File downloaded: ${lastBackupRun.details.fileName} | Collections: ${lastBackupRun.details.collectionCount || 'N/A'}`
                                                :
                                                `Collections: ${lastBackupRun.details.collectionCount} | Documents: ${lastBackupRun.details.documentCount}`
                                            }
                                        </Typography>
                                    )}
                                </Paper>
                            )}
                        </Paper>

                        <Accordion
                            expanded={activityLogsExpanded}
                            onChange={(_, expanded) => setActivityLogsExpanded(expanded)}
                            sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                                <Typography sx={{ fontWeight: 700 }}>Activity Logs (Database)</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
                                    <Grid size={{ md: 7 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                            {activityLastRefreshedAt
                                                ? `Last refreshed: ${new Date(activityLastRefreshedAt).toLocaleString()}`
                                                : 'Use Apply to fetch logs manually.'}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ md: 5 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: { xs: 'left', md: 'right' } }}>
                                            {activityRetentionLoading
                                                ? 'Checking retention window...'
                                                : activityRetentionInfo?.oldCount
                                                    ? `${activityRetentionInfo.oldCount} log(s) older than ${ACTIVITY_RETENTION_DAYS} days`
                                                    : `No logs older than ${ACTIVITY_RETENTION_DAYS} days`}
                                        </Typography>
                                    </Grid>
                                </Grid>

                                {activityRetentionInfo?.oldCount > 0 && !activityRetentionDismissed && (
                                    <Alert severity="warning" sx={{ mb: 2 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                            {activityRetentionInfo.oldCount} activity log(s) are older than {ACTIVITY_RETENTION_DAYS} days.
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                            Oldest retained event: {activityRetentionInfo.oldestEntry?.timestamp ? new Date(activityRetentionInfo.oldestEntry.timestamp).toLocaleString() : 'N/A'}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.25 }}>
                                            <Button size="small" variant="outlined" onClick={() => setActivityRetentionDismissed(true)}>
                                                Keep for now
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                color="warning"
                                                disabled={activityDeleteLoading}
                                                onClick={deleteOldActivityLogs}
                                            >
                                                {activityDeleteLoading ? <CircularProgress size={16} color="inherit" /> : `Delete older than ${ACTIVITY_RETENTION_DAYS} days`}
                                            </Button>
                                        </Box>
                                    </Alert>
                                )}

                                <Grid container spacing={2} alignItems="end" sx={{ mb: 1.5 }}>
                                    <Grid size={{ md: 3 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700 }}>Method</Typography>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="GET / POST / PATCH / DELETE"
                                            value={activityMethodFilter}
                                            onChange={(e) => setActivityMethodFilter(e.target.value)}
                                            sx={inputSx}
                                        />
                                    </Grid>
                                    <Grid size={{ md: 4 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700 }}>Actor UID</Typography>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="Filter by actor UID"
                                            value={activityActorUidFilter}
                                            onChange={(e) => setActivityActorUidFilter(e.target.value)}
                                            sx={inputSx}
                                        />
                                    </Grid>
                                    <Grid size={{ md: 3 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700 }}>Path contains</Typography>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="e.g. invoice"
                                            value={activityPathFilter}
                                            onChange={(e) => setActivityPathFilter(e.target.value)}
                                            sx={inputSx}
                                        />
                                    </Grid>
                                    <Grid size={{ md: 2 }}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            color="warning"
                                            disabled={activityLogsLoading}
                                            onClick={() => refreshActivityAdminData()}
                                        >
                                            {activityLogsLoading ? <CircularProgress size={18} color="inherit" /> : 'Apply'}
                                        </Button>
                                    </Grid>
                                </Grid>

                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                                    <Button variant="outlined" size="small" onClick={selectVisibleActivityLogs} disabled={activityLogs.length === 0}>
                                        Select visible
                                    </Button>
                                    <Button variant="outlined" size="small" onClick={clearSelectedActivityLogs} disabled={selectedActivityLogIds.length === 0}>
                                        Clear selection
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        color="warning"
                                        disabled={selectedActivityLogIds.length === 0 || activityDeleteLoading}
                                        onClick={() => deleteActivityLogs(selectedActivityLogIds)}
                                    >
                                        {activityDeleteLoading ? <CircularProgress size={16} color="inherit" /> : `Delete selected (${selectedActivityLogIds.length})`}
                                    </Button>
                                </Box>

                                <Paper variant="outlined" sx={{ maxHeight: 420, overflowY: 'auto', p: 1.5 }}>
                                    {activityLogsLoading && activityLogs.length === 0 ? (
                                        <ListLoadingSkeleton count={6} height={52} />
                                    ) : activityLogs.length === 0 ? (
                                        <EmptyState title="No activity logs" description="No matching events found in database." />
                                    ) : (
                                        activityLogs.map((entry) => {
                                            const isExpanded = expandedActivityLogId === entry.id;
                                            return (
                                                <Box key={entry.id} sx={{ p: 1.25, mb: 1, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: isExpanded ? 'action.hover' : 'transparent' }}>
                                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                                        <Checkbox
                                                            size="small"
                                                            checked={selectedActivityLogIds.includes(entry.id)}
                                                            onChange={() => toggleActivityLogSelection(entry.id)}
                                                            sx={{ mt: 0.5, flexShrink: 0 }}
                                                        />
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 0.5 }}>
                                                                <Chip size="small" label={entry.method || 'N/A'} color="warning" variant="outlined" />
                                                                <Chip size="small" label={entry.path || 'N/A'} variant="outlined" sx={{ maxWidth: '100%' }} />
                                                                <Chip size="small" label={`Status ${entry.statusCode || '-'}`} variant="outlined" />
                                                            </Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                                                                {entry.message || 'No message'}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
                                                                Actor: {entry.actor_name || 'Unknown'} ({entry.actor_uid || 'N/A'})
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
                                                                IP: {entry.ip_public || entry.ip_local || entry.ip || 'N/A'}
                                                                {entry.ip_public ? ` (Public)` : ''}
                                                                {!entry.ip_public && entry.ip_local ? ` (Local)` : ''}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
                                                                Time: {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : 'N/A'} | Duration: {entry.durationMs || 0}ms
                                                            </Typography>
                                                            
                                                            {/* Expanded Details */}
                                                            {isExpanded && entry.request_body && (
                                                                <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>📋 Request Details:</Typography>
                                                                    <Box sx={{ 
                                                                        bgcolor: 'background.default',
                                                                        p: 1,
                                                                        borderRadius: 1,
                                                                        fontFamily: 'monospace',
                                                                        fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                                                        overflowX: 'auto',
                                                                        maxHeight: 200,
                                                                        overflowY: 'auto',
                                                                        whiteSpace: 'pre-wrap',
                                                                        wordBreak: 'break-word'
                                                                    }}>
                                                                        {JSON.stringify(entry.request_body, null, 2)}
                                                                    </Box>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                        <Box sx={{ display: 'flex', gap: 0.5, flexDirection: 'column', flexShrink: 0, width: { xs: '100%', sm: 'auto' }, mt: { xs: 0.5, sm: 0 } }}>
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                onClick={() => setExpandedActivityLogId(isExpanded ? null : entry.id)}
                                                                sx={{ minWidth: 'auto', p: 0.5, width: { xs: '100%', sm: 'auto' } }}
                                                                title={isExpanded ? 'Hide details' : 'Show details'}
                                                            >
                                                                {isExpanded ? <KeyboardArrowUpRoundedIcon fontSize="small" /> : <KeyboardArrowDownRoundedIcon fontSize="small" />}
                                                            </Button>
                                                            <Button
                                                                variant="outlined"
                                                                color="warning"
                                                                size="small"
                                                                disabled={activityDeleteLoading}
                                                                onClick={() => deleteActivityLogs([entry.id])}
                                                                sx={{ width: { xs: '100%', sm: 'auto' } }}
                                                            >
                                                                Delete
                                                            </Button>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            );
                                        })
                                    )}
                                </Paper>

                                <Box sx={{ mt: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Showing {activityLogs.length} log entries
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        disabled={!activityLogsHasMore || activityLogsLoading}
                                        onClick={() => fetchActivityLogs({ reset: false })}
                                    >
                                        {activityLogsLoading ? <CircularProgress size={16} color="inherit" /> : 'Load More'}
                                    </Button>
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                        )}
                </AuthWrapper>
            </div>
        </>
    )
}

export default AdminPanel

